import {useMemo} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'

import {BreakpointSize, Hero, InlineLink, useWindowSize, Text} from '@primer/react-brand'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {GenericContent} from '../../../../../brand/lib/types/contentful'
import {cohortFunnelBuilder} from '../../../../../lib/analytics'

import styles from './HeroContent.module.css'

type Props = {
  contentfulContent: GenericContent
}

export function HeroContent(props: Props) {
  const {contentfulContent} = props
  const {label, heading, cta, content, subheading} = contentfulContent.fields

  const {
    hasCopilot = false,
    hasCopilotPaid = false,
    hasCopilotPro = false,
    isLoggedIn = false,
  } = useRoutePayload<{
    hasCopilot: boolean
    hasCopilotPaid: boolean
    hasCopilotPro: boolean
    isLoggedIn: boolean
  }>()

  let primaryCta = cta?.fields.default?.fields.primary
  let secondaryCta = cta?.fields.default?.fields.secondary

  if (cta && isLoggedIn) {
    const ctaOptions = [
      {condition: hasCopilotPro, cta: cta.fields.hasCopilotPro},
      {condition: hasCopilotPaid, cta: cta.fields.hasCopilotPaid},
      {condition: hasCopilot, cta: cta.fields.hasCopilot},
      {condition: true, cta: cta.fields.isLoggedIn},
    ]

    const selectedCta = ctaOptions.find(option => option.condition && option.cta)?.cta

    if (selectedCta) {
      primaryCta = selectedCta.fields.primary
      secondaryCta = selectedCta.fields.secondary
    }
  }

  const tertiaryCta = content?.find(item => item.fields.id === 'featuresCopilotHeroTertiaryCta') as
    | GenericContent
    | undefined

  const windowSize = useWindowSize()
  const isDesktopView = useMemo(
    () =>
      [BreakpointSize.MEDIUM, BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ),
    [windowSize.currentBreakpointSize],
  )

  const cft = `copilot_${isLoggedIn ? 'li' : 'lo'}.features_copilot`
  const withCft = cohortFunnelBuilder(cft)

  return (
    <Hero data-hpc align="center" className={styles.hero}>
      {label ? <Hero.Label>{label.fields.text}</Hero.Label> : null}

      {heading ? <Hero.Heading size="1">{heading}</Hero.Heading> : null}

      {subheading ? (
        <Hero.Description size="350" variant="default">
          {documentToPlainTextString(subheading)}
        </Hero.Description>
      ) : null}

      <div className={styles.buttons}>
        {primaryCta && (
          <Hero.PrimaryAction
            href={primaryCta.fields.href}
            {...getAnalyticsEvent({
              action: primaryCta.fields.text,
              tag: 'button',
              context: 'CTAs',
              location: 'hero',
            })}
          >
            {primaryCta.fields.text}
          </Hero.PrimaryAction>
        )}

        {secondaryCta ? (
          <Hero.SecondaryAction
            href={withCft(secondaryCta.fields.href)}
            variant={secondaryCta.fields.variant === 'accent' ? 'primary' : secondaryCta.fields.variant || 'subtle'}
            {...getAnalyticsEvent({
              action: secondaryCta.fields.text,
              tag: 'button',
              context: 'CTAs',
              location: 'hero',
            })}
          >
            {secondaryCta.fields.text}
          </Hero.SecondaryAction>
        ) : null}
      </div>

      {isDesktopView && tertiaryCta && tertiaryCta.fields.text ? (
        <Text weight="normal" style={{marginTop: '1rem'}}>
          {documentToReactComponents(tertiaryCta.fields.text, {
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => [children],
              [INLINES.HYPERLINK]: (node, children) => (
                <InlineLink
                  href={node.data.uri}
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                  className="tertiary-cta"
                  {...getAnalyticsEvent({
                    action: documentToPlainTextString(node, ' '),
                    tag: 'link',
                    context: 'CTAs',
                    location: 'hero',
                  })}
                >
                  {children}
                </InlineLink>
              ),
            },
          })}
        </Text>
      ) : null}
    </Hero>
  )
}
